import {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import type {TFunction} from "i18next";
import {Card, Group, Stack, Text, UnstyledButton} from "@mantine/core";
import IconChevronRight from "../../../assets/chevron-right.svg?react";
import IconDownload from "../../../assets/download.svg?react";
import IconSearch from "../../../assets/search.svg?react";
import {ListSource, PostSearchState} from "../../../types";
import {TOPIC_TYPE_PATHS} from "../../../paths";
import {getLinkTypeSlug, resolveTypeLabel} from "../../content/typeIcons";
import {fileExtensionMap, isValidFileType} from "../../content/contentUtils";
import {ResourceType} from "../../content/types";
import {pdfTopicHref, resolveDibHref} from "../../../utils/dibUrls";
import {ExperimentalLink} from "../../../components/ExperimentalLink";
import {bffApi} from "../bffApi";
import {metricUuid, newMetricEvent} from "../metrics";
import {noteTopicEntry} from "../../content/topicEntryNote";
import classes from "./PostSearchTabPanel.module.css";

interface PostSearchTabPanelProps {
    state: PostSearchState | null;
    // Which backend list this panel renders. The two tabs (Verktøy / Kilder)
    // map directly onto the two ranked lists the backend emits — no merging.
    kind: "tools" | "sources";
}

interface CategoryBucket {
    key: string;
    label: string;
    count: number;
}

const QUERY_CAP = 80;
const capQuery = (q: string): string => {
    if (q.length <= QUERY_CAP) return q;
    const slice = q.slice(0, QUERY_CAP);
    const lastSpace = slice.lastIndexOf(" ");
    return `${(lastSpace > 0 ? slice.slice(0, lastSpace) : slice).trimEnd()}…`;
};

/** The download formats the metrics endpoint accepts; anything else is "unknown". */
const downloadFormat = (extension: string): "docx" | "xlsx" | "pdf" | "zip" | "unknown" => {
    const format = extension.replace(/^\./, "");
    return format === "docx" || format === "xlsx" || format === "pdf" || format === "zip"
        ? format
        : "unknown";
};

const categorySlug = (item: ListSource): string => {
    const byId =
        item.topicTypeid != null
            ? TOPIC_TYPE_PATHS[
                  item.topicTypeid as keyof typeof TOPIC_TYPE_PATHS
              ]
            : undefined;
    return (
        byId ??
        getLinkTypeSlug(item.url) ??
        item.topicType?.toLowerCase() ??
        "unknown"
    );
};

const buildBuckets = (items: ListSource[], t: TFunction): CategoryBucket[] => {
    const map = new Map<string, {label: string; count: number}>();
    for (const item of items) {
        const key = categorySlug(item);
        const existing = map.get(key);
        if (existing) {
            existing.count++;
        } else {
            map.set(key, {
                label: resolveTypeLabel(t, item.topicType, item.url) || key,
                count: 1,
            });
        }
    }
    return Array.from(map.entries())
        .map(([key, {label, count}]) => ({key, label, count}))
        .sort((a, b) => b.count - a.count);
};

const ResultCard = ({
    item,
    onOpen,
}: {
    item: ListSource;
    /** `download` is the file extension when the hit downloads instead of opening a page. */
    onOpen: (download: string | null) => void;
}) => {
    const {t} = useTranslation();
    const badgeLabel = resolveTypeLabel(t, item.topicType, item.url);
    // A PDF hit opens in the in-app viewer at its cited position.
    const isPdfInApp =
        Boolean(item.topicId) &&
        String(item.resourceTypeid) === ResourceType.pdf;
    // The backend's url is the path for every hit, files included — only the
    // download affordance below is built locally.
    const href = isPdfInApp
        ? pdfTopicHref(item.url, item.topicTitle, item.topicId, item)
        : resolveDibHref(item.url);
    const isFile =
        !isPdfInApp &&
        isValidFileType(item.resourceTypeid as unknown as ResourceType);

    const title = item.segmentTitle
        ? `${item.topicTitle} | ${item.segmentTitle}`
        : item.topicTitle;
    const subText = item.description || badgeLabel;

    const body = (
        <>
            <span className={classes.resultBody}>
                <span className={classes.resultTitle}>{title}</span>
                <span className={classes.resultSub}>{subText}</span>
            </span>
            <span className={classes.typeBadge}>{badgeLabel}</span>
            {isFile ? (
                <IconDownload className={classes.resultChevron} />
            ) : (
                <IconChevronRight className={classes.resultChevron} />
            )}
        </>
    );

    const downloadName = isFile
        ? `${item.topicTitle}${fileExtensionMap[String(item.resourceTypeid)] ?? ""}`
        : undefined;

    return (
        <ExperimentalLink
            href={href}
            download={downloadName}
            className={classes.resultCard}
            onClick={() =>
                onOpen(isFile ? (fileExtensionMap[String(item.resourceTypeid)] ?? "") : null)
            }
        >
            {body}
        </ExperimentalLink>
    );
};

export function PostSearchTabPanel({state, kind}: PostSearchTabPanelProps) {
    const {t} = useTranslation();
    const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
    const [queryExpanded, setQueryExpanded] = useState(false);
    const [trackInteraction] = bffApi.endpoints.trackInteraction.useMutation();

    const items = useMemo(() => {
        const source = kind === "tools" ? state?.tools : state?.otherSources;
        const seen = new Set<string>();
        const out: ListSource[] = [];
        for (const item of source ?? []) {
            const dedupeKey = item.url || item.topicId;
            if (!dedupeKey || seen.has(dedupeKey)) continue;
            seen.add(dedupeKey);
            out.push(item);
        }
        return out;
    }, [kind, state?.tools, state?.otherSources]);

    const buckets = useMemo(() => buildBuckets(items, t), [items, t]);

    const filtered = useMemo(() => {
        if (!selectedFilter) return items;
        return items.filter((item) => categorySlug(item) === selectedFilter);
    }, [items, selectedFilter]);

    const toggleFilter = (key: string) => {
        setSelectedFilter((prev) => (prev === key ? null : key));
    };

    const isSearching = state?.status === "searching";
    const query = state?.query ?? "";

    return (
        <div className={classes.root}>
            {buckets.length > 0 && (
                <div className={classes.filterChips}>
                    {buckets.map((b) => {
                        const active = selectedFilter === b.key;
                        return (
                            <UnstyledButton
                                key={b.key}
                                className={`${classes.chip} ${active ? classes.chipActive : ""}`}
                                onClick={() => toggleFilter(b.key)}
                                aria-pressed={active}
                            >
                                {b.label}
                                <span className={classes.chipCount}>
                                    {b.count}
                                </span>
                            </UnstyledButton>
                        );
                    })}
                </div>
            )}
            <Card withBorder radius={0} p={0} className={classes.card}>
                <div className={classes.header}>
                    <Group gap="xs" wrap="nowrap" align="center">
                        <IconSearch
                            width={16}
                            height={16}
                            className={classes.headerIcon}
                        />
                        <Text
                            size="sm"
                            fw={700}
                            c="dark.5"
                            className={`${classes.resultsForLabel} ${queryExpanded ? "" : classes.clamped}`}
                            onClick={
                                query.length > QUERY_CAP
                                    ? () => setQueryExpanded((v) => !v)
                                    : undefined
                            }
                            style={
                                query.length > QUERY_CAP
                                    ? {cursor: "pointer"}
                                    : undefined
                            }
                        >
                            {t("thread.postsearch.resultsFor", {
                                defaultValue: "Search results for",
                            })}{" "}
                            {`"${queryExpanded ? query : capQuery(query)}"`}
                        </Text>
                    </Group>
                </div>
                <div className={classes.results}>
                    {filtered.length === 0 && !isSearching ? (
                        <Text size="sm" c="dimmed" p="md">
                            {t("artifact.searchResults.noResults", {
                                query: state?.query ?? "",
                            })}
                        </Text>
                    ) : (
                        <Stack gap={0}>
                            {filtered.map((item, index) => (
                                <ResultCard
                                    key={item.url}
                                    item={item}
                                    onOpen={(download) => {
                                        // The topic page records the open and
                                        // its entry source; a download has no page.
                                        noteTopicEntry(item.topicId, "search");
                                        if (download !== null) {
                                            void trackInteraction({
                                                ...newMetricEvent(),
                                                action: "file_download_requested",
                                                subjectId: metricUuid(item.topicId),
                                                source: "search_result",
                                                format: downloadFormat(download),
                                            });
                                        }
                                    }}
                                />
                            ))}
                        </Stack>
                    )}
                </div>
            </Card>
        </div>
    );
}
